import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  IconButton,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  FormLabel,
  FormControl,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Input,
  Icon,
  useToast,
} from "@chakra-ui/react";
import { FaEdit, FaTrash, FaEye, FaPlus } from "react-icons/fa";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const TableCategory = () => {
  const { isOpen, onOpen, onClose } = useDisclosure(); // Modal for viewing events
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure(); // Modal for editing an event
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [updatedEvent, setUpdatedEvent] = useState({
    event_master: "",
    description: "",
  });
  const schoolId = sessionStorage.getItem("school_id");
  const toast = useToast(); // Chakra UI toast for notifications

  // Fetch the list of events
  const fetchEvents = () => {
    setLoading(true);
    fetch(
      `${BASE_URL}/admin/events/event_master/list.php?school_id=${schoolId}`
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.success) {
          setEvents(result.data);
        } else {
          toast({
            title: "Error",
            description: result.message || "Failed to fetch events.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching events:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchEvents(); // Load events on component mount
  }, []);

  // Handle Edit click to pre-fill event data in the modal
  const handleEdit = (event) => {
    setSelectedEvent(event);
    setUpdatedEvent({
      event_master: event.event_master,
      description: event.description || "",
    });
    onEditOpen();
  };

  // Handle saving the updated event
  const handleUpdate = () => {
    if (!updatedEvent.event_master.trim() || !updatedEvent.description.trim()) {
      toast({
        title: "Error",
        description: "All fields are required.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const data = {
      user_id: sessionStorage.getItem("user_id"), // Replace with actual user ID
      school_id: schoolId,
      event_id: selectedEvent.id,
      event_master: updatedEvent.event_master,
      description: updatedEvent.description,
    };

    fetch(`${BASE_URL}/admin/events/event_master/edit.php`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((result) => {
        toast({
          title: result.success ? "Success" : "Error",
          description: result.message,
          status: result.success ? "success" : "error",
          duration: 5000,
          isClosable: true,
        });
        if (result.success) {
          fetchEvents(); // Refresh the list after editing
          onEditClose(); // Close the modal after successful update
        }
      })
      .catch((error) => console.error("Error editing event:", error));
  };

  // Handle deleting an event
  const handleDelete = (eventId) => {
    const data = {
      user_id: sessionStorage.getItem("user_id"), // Replace with actual user ID
      school_id: schoolId,
      event_id: eventId,
    };

    fetch(`${BASE_URL}/admin/events/event_master/delete.php`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((result) => {
        toast({
          title: result.success ? "Success" : "Error",
          description: result.message,
          status: result.success ? "success" : "error",
          duration: 5000,
          isClosable: true,
        });
        if (result.success) {
          fetchEvents(); // Refresh the list after deleting
        }
      })
      .catch((error) => console.error("Error deleting event:", error));
  };

  return (
    <div>
      {/* View Button */}
      <Button
        leftIcon={<FaEye />}
        colorScheme="blue"
        variant="solid"
        onClick={onOpen}
        size={{ base: "xs", md: "md" }}
      ></Button>

      {/* Modal with the Table of Events */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Event Categories
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {loading ? (
              <Center>
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : (
              <Table
                sx={{
                  "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
                }}
              >
                <Thead>
                  <Tr>
                    <Th>Category Name</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {events.length > 0 ? (
                    events.map((event) => (
                      <Tr key={event.id}>
                        <Td>{event.event_master}</Td>
                        <Td>
                          <IconButton
                            icon={<FaEdit />}
                            colorScheme="yellow"
                            aria-label="Edit Event"
                            onClick={() => handleEdit(event)}
                            marginRight={2}
                          />
                          <IconButton
                            icon={<FaTrash />}
                            colorScheme="red"
                            aria-label="Delete Event"
                            onClick={() => handleDelete(event.id)}
                          />
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan="2" textAlign="center">
                        No events found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Edit Event Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Event</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl marginBottom={4}>
              <FormLabel>Event Name</FormLabel>
              <Input
                value={updatedEvent.event_master}
                onChange={(e) =>
                  setUpdatedEvent({
                    ...updatedEvent,
                    event_master: e.target.value,
                  })
                }
                placeholder="Enter the event name"
              />
            </FormControl>
            <FormControl marginBottom={4}>
              <FormLabel>Event Description</FormLabel>
              <Input
                value={updatedEvent.description}
                onChange={(e) =>
                  setUpdatedEvent({
                    ...updatedEvent,
                    description: e.target.value,
                  })
                }
                placeholder="Enter the event description"
              />
            </FormControl>
            <Flex justifyContent="flex-end" mt={4}>
              <Button colorScheme="blue" onClick={handleUpdate}>
                Save Changes
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default TableCategory;
